import { useMemo } from 'react';
import { useLatestLevelFromPageNow } from './bin-props';

/**
 * Hook for local search and sorting of bin data
 * @param {Array} bins - Array of bin entities
 * @param {string} query - Search query string
 * @param {string} sort - Sort field ('name', 'volume', 'percent', 'material')
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Object} Filtered and sorted bins with loading state
 */
export function useLocalSearchSort(bins = [], query = '', sort = 'name', order = 'asc') {
  const processedBins = useMemo(() => {
    if (!bins || bins.length === 0) {
      return { filteredBins: [], loading: false };
    }

    // First, filter by search query
    let filteredBins = bins;

    if (query && query.trim()) {
      const searchTerm = query.trim().toLowerCase();
      filteredBins = bins.filter(binEntity => {
        const properties = binEntity.properties;
        const name = properties.name?.toLowerCase() || '';

        // Search in bin name
        if (name.includes(searchTerm)) {
          return true;
        }

        // Search in material (if available)
        const material = properties.material?.toLowerCase() || '';
        if (material.includes(searchTerm)) {
          return true;
        }

        // Search in bin type (if available)
        const binType = properties.binType?.toLowerCase() || '';
        if (binType.includes(searchTerm)) {
          return true;
        }

        return false;
      });
    }

    // Then sort the filtered results
    const sortedBins = [...filteredBins].sort((a, b) => {
      let aValue, bValue;

      switch (sort) {
        case 'name':
          aValue = a.properties.name || '';
          bValue = b.properties.name || '';
          break;

        case 'volume':
          // Get current volume for sorting
          aValue = a.properties.volumes?.fullTotal || 0;
          bValue = b.properties.volumes?.fullTotal || 0;
          break;

        case 'percent':
          // Calculate fill percentage for sorting
          const aLatestVolume = a.properties.volumes?.fullTotal || 0;
          const aFullTotal = a.properties.volumes?.fullTotal || 1;
          const bLatestVolume = b.properties.volumes?.fullTotal || 0;
          const bFullTotal = b.properties.volumes?.fullTotal || 1;

          aValue = aLatestVolume / aFullTotal;
          bValue = bLatestVolume / bFullTotal;
          break;

        case 'material':
          aValue = a.properties.material || '';
          bValue = b.properties.material || '';
          break;

        default:
          aValue = a.properties.name || '';
          bValue = b.properties.name || '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return order === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return { filteredBins: sortedBins, loading: false };
  }, [bins, query, sort, order]);

  return processedBins;
}

/**
 * Hook for local search and sorting of processed bin data (for map view)
 * @param {Array} binsForMap - Array of processed bin data for map
 * @param {string} query - Search query string
 * @param {string} sort - Sort field ('name', 'volume', 'percent', 'material')
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Object} Filtered and sorted bins with loading state
 */
export function useLocalSearchSortMap(binsForMap = [], query = '', sort = 'name', order = 'asc') {
  const processedBins = useMemo(() => {
    if (!binsForMap || binsForMap.length === 0) {
      return { filteredBins: [], loading: false };
    }

    // First, filter by search query
    let filteredBins = binsForMap;

    if (query && query.trim()) {
      const searchTerm = query.trim().toLowerCase();
      filteredBins = binsForMap.filter(bin => {
        const name = bin.name?.toLowerCase() || '';
        const material = bin.material?.toLowerCase() || '';
        const binType = bin.binType?.toLowerCase() || '';

        return name.includes(searchTerm) ||
               material.includes(searchTerm) ||
               binType.includes(searchTerm);
      });
    }

    // Then sort the filtered results
    const sortedBins = [...filteredBins].sort((a, b) => {
      let aValue, bValue;

      switch (sort) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;

        case 'volume':
          aValue = a.latestVolume || 0;
          bValue = b.latestVolume || 0;
          break;

        case 'percent':
          // Calculate fill percentage for sorting
          const aLatestVolume = a.latestVolume || 0;
          const aFullTotal = a.fullTotal || 1;
          const bLatestVolume = b.latestVolume || 0;
          const bFullTotal = b.fullTotal || 1;

          aValue = aLatestVolume / aFullTotal;
          bValue = bLatestVolume / bFullTotal;
          break;

        case 'material':
          aValue = a.material || '';
          bValue = b.material || '';
          break;

        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return order === 'asc' ? comparison : -comparison;
      }

      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return { filteredBins: sortedBins, loading: false };
  }, [binsForMap, query, sort, order]);

  return processedBins;
}
